import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ExpertApplication {
  id: string;
  user_id: string | null;
  full_name: string;
  email: string;
  phone: string | null;
  professional_title: string;
  years_experience: number;
  specializations: string[];
  bio: string;
  hourly_rate: number;
  linkedin_url: string | null;
  website_url: string | null;
  motivation_letter: string | null;
  status: 'pending' | 'approved' | 'rejected';
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  profile_image_url: string | null;
}

export function useApplicationStatusUpdater(onRefresh: () => void) {
  const handleStatusUpdate = async (
    applicationId: string, 
    status: 'approved' | 'rejected', 
    adminNotes: string,
    selectedApplication: ExpertApplication | null
  ) => {
    try {
      console.log(`🔄 Updating application ${applicationId} to ${status}...`);
      
      // Get current user for reviewer info
      const { data: { user } } = await supabase.auth.getUser();
      
      // Update the application status
      const { error: updateError } = await supabase
        .from("expert_applications")
        .update({
          status,
          admin_notes: adminNotes || null,
          reviewed_by: user?.id || null,
          reviewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq("id", applicationId);

      if (updateError) throw updateError;

      // If approved, create the expert profile
      if (status === 'approved' && selectedApplication) {
        console.log("✅ Application approved, creating expert profile...");
        
        // Check if expert profile already exists for this user
        const expertUserId = selectedApplication.user_id || user?.id || crypto.randomUUID();
        
        const { data: existingExpert, error: checkError } = await supabase
          .from("consultation_experts")
          .select("id, application_id")
          .eq("user_id", expertUserId)
          .maybeSingle();

        if (checkError && checkError.code !== 'PGRST116') {
          console.error("❌ Error checking existing expert:", checkError);
          throw checkError;
        }

        if (!existingExpert) {
          console.log("📝 Creating new expert profile for application:", applicationId);
          
          // Create new expert profile with inactive status
          const { data: newExpert, error: expertError } = await supabase
            .from("consultation_experts")
            .insert({
              application_id: applicationId,
              user_id: expertUserId,
              full_name: selectedApplication.full_name,
              professional_title: selectedApplication.professional_title,
              bio: selectedApplication.bio,
              years_experience: selectedApplication.years_experience,
              specializations: selectedApplication.specializations,
              hourly_rate: selectedApplication.hourly_rate,
              linkedin_url: selectedApplication.linkedin_url,
              website_url: selectedApplication.website_url,
              status: 'inactive', // Start as inactive until booking link is added
              admin_notes: adminNotes || null,
              approved_by: user?.id || null,
              approved_at: new Date().toISOString(),
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              profile_image_url: selectedApplication.profile_image_url,
            })
            .select('id')
            .single();

          if (expertError) {
            console.error("❌ Error creating expert profile:", expertError);
            throw expertError;
          }

          console.log("✅ Expert profile created successfully with ID:", newExpert?.id);
        } else {
          console.log(`ℹ️ Expert profile already exists for user ${expertUserId} with ID: ${existingExpert.id} (from application: ${existingExpert.application_id})`);
          console.log("ℹ️ Updating existing expert profile to link to new approved application");
          
          // Update the existing expert profile to link to the new approved application
          const { error: updateError } = await supabase
            .from("consultation_experts")
            .update({
              application_id: applicationId,
              full_name: selectedApplication.full_name,
              professional_title: selectedApplication.professional_title,
              bio: selectedApplication.bio,
              years_experience: selectedApplication.years_experience,
              specializations: selectedApplication.specializations,
              hourly_rate: selectedApplication.hourly_rate,
              linkedin_url: selectedApplication.linkedin_url,
              website_url: selectedApplication.website_url,
              admin_notes: adminNotes || null,
              approved_by: user?.id || null,
              approved_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              profile_image_url: selectedApplication.profile_image_url,
            })
            .eq("id", existingExpert.id);

          if (updateError) {
            console.error("❌ Error updating existing expert profile:", updateError);
            throw updateError;
          }

          console.log("✅ Updated existing expert profile to link to new application:", applicationId);
        }
      }

      console.log(`✅ Application ${status} successfully`);
      toast.success(`Application ${status} successfully`);
      onRefresh();
    } catch (error) {
      console.error(`💥 Error updating application status:`, error);
      toast.error(`Failed to ${status.toLowerCase()} application`);
    }
  };

  // New function to create missing expert profiles for already approved applications
  const createMissingExpertProfiles = async () => {
    try {
      console.log("🔄 Checking for approved applications without expert profiles...");
      
      // Get current user for reviewer info
      const { data: { user } } = await supabase.auth.getUser();
      
      // Get all approved applications
      const { data: approvedApps, error: appsError } = await supabase
        .from("expert_applications")
        .select("*")
        .eq("status", "approved");

      if (appsError) throw appsError;

      console.log("📊 Found approved applications:", approvedApps?.length || 0);

      if (!approvedApps || approvedApps.length === 0) return;

      // Check which ones don't have expert profiles
      for (const app of approvedApps) {
        // Check if expert profile already exists for this user
        const expertUserId = app.user_id || user?.id || crypto.randomUUID();
        
        const { data: existingExpert, error: checkError } = await supabase
          .from("consultation_experts")
          .select("id, application_id")
          .eq("user_id", expertUserId)
          .maybeSingle();

        if (checkError && checkError.code !== 'PGRST116') {
          console.error("❌ Error checking expert for app:", app.id, checkError);
          continue;
        }

        if (!existingExpert) {
          console.log("📝 Creating missing expert profile for approved application:", app.id);
          
          const { error: expertError } = await supabase
            .from("consultation_experts")
            .insert({
              application_id: app.id,
              user_id: expertUserId,
              full_name: app.full_name,
              professional_title: app.professional_title,
              bio: app.bio,
              years_experience: app.years_experience,
              specializations: app.specializations,
              hourly_rate: app.hourly_rate,
              linkedin_url: app.linkedin_url,
              website_url: app.website_url,
              status: 'inactive', // Start as inactive until booking link is added
              admin_notes: app.admin_notes,
              approved_by: user?.id || null,
              approved_at: new Date().toISOString(),
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              profile_image_url: (app as any).profile_image_url || null,
            });

          if (expertError) {
            console.error("❌ Error creating missing expert profile:", expertError);
          } else {
            console.log("✅ Created missing expert profile for application:", app.id);
          }
        } else {
          console.log(`ℹ️ Expert profile already exists for user ${expertUserId} (from application: ${existingExpert.application_id})`);
          console.log("ℹ️ Updating existing expert profile to link to latest approved application:", app.id);
          
          // Update the existing expert profile to link to the latest approved application
          const { error: updateError } = await supabase
            .from("consultation_experts")
            .update({
              application_id: app.id,
              full_name: app.full_name,
              professional_title: app.professional_title,
              bio: app.bio,
              years_experience: app.years_experience,
              specializations: app.specializations,
              hourly_rate: app.hourly_rate,
              linkedin_url: app.linkedin_url,
              website_url: app.website_url,
              admin_notes: app.admin_notes,
              approved_by: user?.id || null,
              approved_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              profile_image_url: (app as any).profile_image_url || null,
            })
            .eq("id", existingExpert.id);

          if (updateError) {
            console.error("❌ Error updating existing expert profile:", updateError);
          } else {
            console.log("✅ Updated existing expert profile to link to application:", app.id);
          }
        }
      }

      console.log("✅ Finished checking for missing expert profiles");
    } catch (error) {
      console.error("💥 Error creating missing expert profiles:", error);
    }
  };

  return { 
    handleStatusUpdate,
    createMissingExpertProfiles
  };
}
